/**
 * A date and a time in one field, picked rather than typed.
 *
 * The Offers form used to ask for `2026-09-17T10:00` in a text box, which is a
 * format to remember and a format to get wrong. This is the replacement: a
 * button that shows the value in the merchant's own language, and a popover with
 * the shared month grid and an hour and a minute.
 *
 * The value format is unchanged from the text field it replaces —
 * `YYYY-MM-DDTHH:MM`, local, no seconds — so the form state and the payload are
 * untouched.
 */

import { useState } from 'react';
import { Pressable } from 'react-native';
import { CalendarDays } from 'lucide-react-native';

import {
  Calendar,
  pad,
  parseIsoDate,
  toIsoDate,
  todayParts,
  type CalendarMonth,
} from './calendar';
import { Field, TimeSelects } from './fields';
import { Row, Stack } from './layout';
import { Button } from './ui/button';
import { Icon } from './ui/icon';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from './ui/popover';
import { Text } from './ui/text';
import { useLocale } from '../providers/locale';
import { cn } from '../lib/utils';

/** The local fields of a `YYYY-MM-DDTHH:MM` value. */
interface LocalDateTime {
  year: number;
  /** Zero-based, as `Date` holds it. */
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** The one format the form holds and writes. */
const VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * Reads a `YYYY-MM-DDTHH:MM` value into its parts, without going through
 * `Date` — parsing a local string with `new Date` is where a timezone bug gets
 * in.
 *
 * @param value The form's value.
 * @returns The parts, or null when the value is empty or malformed.
 */
function parseValue(value: string): LocalDateTime | null {
  const match = VALUE_PATTERN.exec(value.trim());
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

/**
 * Writes the parts back in the form's format.
 *
 * @param parts The date and time.
 * @returns A `YYYY-MM-DDTHH:MM` string.
 */
function formatValue(parts: LocalDateTime): string {
  return `${toIsoDate(parts.year, parts.month, parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

export interface DateTimeFieldProps {
  label?: string;
  /** The current value, as `YYYY-MM-DDTHH:MM`. */
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  hint?: string;
  disabled?: boolean;
}

/**
 * A labelled date-and-time picker.
 *
 * @param props The label and chrome, the value, and the change handler.
 * @returns The field.
 */
export function DateTimeField({ label, value, onChange, error, hint, disabled }: DateTimeFieldProps) {
  const { t, locale } = useLocale();
  const parsed = parseValue(value);
  const now = todayParts();
  const current: LocalDateTime = parsed ?? { ...now, hour: 9, minute: 0 };

  // The month on screen, which is not the month of the value while the merchant
  // is browsing. Seeded from the value so opening the field shows its own month.
  const [view, setView] = useState<CalendarMonth>(() => ({
    year: current.year,
    month: current.month,
  }));

  const goToday = () => {
    setView({ year: now.year, month: now.month });
    onChange(formatValue({ ...current, year: now.year, month: now.month, day: now.day }));
  };

  // 24-hour, because that is what the rest of the form speaks — the hour select
  // is 00–23 and the serving-time fields say "24-hour, for example 18:30".
  const display = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(current.year, current.month, current.day, current.hour, current.minute));

  return (
    <Field label={label} hint={hint} error={error}>
      {(control) => (
        <Popover>
          <PopoverTrigger asChild>
            <Pressable
              {...control}
              accessibilityRole="button"
              disabled={disabled}
              className={cn(
                'bg-background border-input min-h-12 flex-row items-center justify-between gap-2 rounded-md border px-3',
                disabled && 'bg-muted opacity-60',
                error && 'border-destructive'
              )}>
              <Text className="text-foreground text-sm">{display}</Text>
              <Icon as={CalendarDays} size={16} className="text-foreground" />
            </Pressable>
          </PopoverTrigger>

          <PopoverContent align="start" className="w-[300px] p-3">
            <Stack gap="sm">
              <Calendar
                value={toIsoDate(current.year, current.month, current.day)}
                month={view}
                onMonthChange={setView}
                onChange={(iso) => {
                  const picked = parseIsoDate(iso);
                  if (picked) {
                    onChange(
                      formatValue({
                        ...current,
                        year: picked.year,
                        month: picked.month,
                        day: picked.day,
                      })
                    );
                  }
                }}
              />

              <TimeSelects
                hour={current.hour}
                minute={current.minute}
                onChange={(hour, minute) => onChange(formatValue({ ...current, hour, minute }))}
              />

              <Row spread align="center" className="border-border border-t pt-2">
                <Button size="sm" variant="ghost" onPress={goToday} accessibilityLabel={t('common.today')}>
                  <Text>{t('common.today')}</Text>
                </Button>
                <PopoverClose
                  accessibilityRole="button"
                  accessibilityLabel={t('common.done')}
                  className="bg-primary h-9 items-center justify-center rounded-md px-3 active:opacity-90">
                  <Text className="text-primary-foreground text-sm font-medium">{t('common.done')}</Text>
                </PopoverClose>
              </Row>
            </Stack>
          </PopoverContent>
        </Popover>
      )}
    </Field>
  );
}
